/**
 * evidenceCanon.js — DEAD SIGNAL
 *
 * Interim canonical-evidence accessor for the UI lane. Every record follows
 * the contract D5 shape exactly: id, legacyId, title, type, chapterId,
 * sceneId, hotspotId, image, description, corruptible, corruptedDescription,
 * bioscanTopic, connections, seededAtChapter.
 *
 * This module mirrors the API the Knowledge lane ships from
 * app/src/content/knowledge/index.js (getEvidence / allEvidence). The three
 * consumers (EvidenceBoard, EvidenceDossier, gameState seed) repoint their
 * imports and this file retires once Lane K's read surface is the single
 * source; until then the ids here are kept in lockstep with the bible.
 *
 * Chapter-3 ids are the ratified Lane K canonical ids (contract K0-4): the ten
 * provisional D4 codes were reconciled to the evidence-bible ids in place (a
 * pure data edit, no consumer refactor), and each renamed record keeps its
 * PE-### as legacyId so prior references still resolve. EVIDENCE_SEED_D6 now
 * lists only ids that resolve in BOTH this accessor and the bible.
 *
 * `connections` stays inside this record set so no id dangles; the full-game
 * graph (BEACON_PHOTOGRAPH and the testimony chains) is restored by the
 * Knowledge lane's evidence-bible, which owns the complete id list.
 */

const EVIDENCE_CANON = {
  // ── Chapter 2 — Delacroix apartment, living-Rena visit (D6 seed) ───────
  // Alive-canon (T-CANON-RECON): on Day 5 Rena Delacroix is alive and working
  // her own investigation. Chapter 2 is her walking Craine through her wall
  // and notebook, not a crime scene. The forensic reads of her apartment
  // (WALL_GAP, WATER_GLASS, RELAY_RECEIPT, OLDTOWN_SOIL) exist only after her
  // death and are ch8 records; they are NOT seeded and never surface in the
  // demo's Day-5 locker.
  DELACROIX_NOTEBOOK: {
    id: 'DELACROIX_NOTEBOOK',
    legacyId: null,
    title: 'Delacroix\'s Case Notebook',
    type: 'DOCUMENT',
    chapterId: 'ch2',
    sceneId: 'delacroix_apt',
    hotspotId: 'da-09',
    image: '/images/evidence/delacroix_notebook.webp',
    description:
      'Rena Delacroix\'s personal case notebook. She walked Craine through it herself: investigation notes, a timeline, and a circled reference to "the photograph from the Beacon archive, the one Castillo wouldn\'t publish."',
    corruptible: false,
    corruptedDescription: null,
    bioscanTopic: null,
    connections: [],
    seededAtChapter: 'ch2',
  },

  // ── Chapter 8 — Delacroix apartment as a crime scene (post-death) ──────
  // Not seeded, not reachable in the demo. Kept so the accessor stays in
  // lockstep with the evidence-bible id list.
  WALL_GAP: {
    id: 'WALL_GAP',
    legacyId: null,
    title: 'Missing Photograph',
    type: 'PHYSICAL',
    chapterId: 'ch8',
    sceneId: 'delacroix_apt',
    hotspotId: 'da-05',
    image: '/images/evidence/wall_gap.webp',
    description:
      'A photograph was removed from Delacroix\'s investigation wall. Four pin holes, tape residue, roughly 15x10 cm. Adhesive residue suggests removal within 72 hours of her death, likely by the killer.',
    corruptible: false,
    corruptedDescription: null,
    bioscanTopic: null,
    connections: ['DELACROIX_NOTEBOOK'],
    seededAtChapter: 'ch8',
  },
  WATER_GLASS: {
    id: 'WATER_GLASS',
    legacyId: null,
    title: 'Unknown Fingerprint (Water Glass)',
    type: 'FORENSIC',
    chapterId: 'ch8',
    sceneId: 'delacroix_apt',
    hotspotId: 'da-08',
    image: '/images/evidence/water_glass.webp',
    description:
      'A water glass with an unidentified fingerprint. Not Delacroix\'s. Not in the NMPD database. Dermal ridge distortion consistent with chronic neural-interface exposure. Someone else was in this apartment.',
    corruptible: true,
    corruptedDescription:
      'A water glass with a fingerprint. Cross-reference: ███████ DATA UNAVAILABLE. Standard residential item. Inconclusive.',
    bioscanTopic: null,
    connections: ['OLDTOWN_SOIL'],
    seededAtChapter: 'ch8',
  },
  RELAY_RECEIPT: {
    id: 'RELAY_RECEIPT',
    legacyId: null,
    title: 'Receipt from The Relay',
    type: 'PHYSICAL',
    chapterId: 'ch8',
    sceneId: 'delacroix_apt',
    hotspotId: 'da-10',
    image: '/images/evidence/relay_receipt.webp',
    description:
      'A receipt from The Relay in the Undertow District, dated September 7, 2057. Two orders of wonton soup. She was meeting someone.',
    corruptible: false,
    corruptedDescription: null,
    bioscanTopic: null,
    connections: ['DELACROIX_NOTEBOOK'],
    seededAtChapter: 'ch8',
  },
  OLDTOWN_SOIL: {
    id: 'OLDTOWN_SOIL',
    legacyId: null,
    title: 'Oldtown Soil Match',
    type: 'FORENSIC',
    chapterId: 'ch8',
    sceneId: 'delacroix_apt',
    hotspotId: 'da-11',
    image: '/images/evidence/oldtown_soil.webp',
    description:
      'Shoe print soil matches Oldtown District geological profile (97.3% confidence). Same composition found at Scene 01 (Nadia Sorel). The killer moves through Oldtown regularly.',
    corruptible: true,
    corruptedDescription:
      'Soil analysis: ██████ minerals, residential origin. Multiple district matches. Inconclusive for geographic tracking.',
    bioscanTopic: null,
    connections: ['WATER_GLASS', 'ST_ERASMUS_SOIL_TRACE'],
    seededAtChapter: 'ch8',
  },

  // ── Chapter 3 — Case file review: St. Erasmus east wing (D6 seed) ──────
  BATTERY_LANTERNS: {
    id: 'BATTERY_LANTERNS',
    legacyId: 'PE-001',
    title: 'East Wing Wide Shot',
    type: 'PHYSICAL',
    chapterId: 'ch3',
    sceneId: 'scene_01_review',
    hotspotId: 's1-01',
    image: '/images/evidence/battery_lanterns.webp',
    description:
      'Crime scene photograph: an abandoned hospital hallway, six battery-powered lanterns arranged in a circle. Commercially available, untraceable, purchased with cash. The killer brought his own light to a decommissioned wing with no power.',
    corruptible: false,
    corruptedDescription: null,
    bioscanTopic: null,
    connections: ['ST_ERASMUS_SOIL_TRACE', 'PARACELSUS_TEXT', 'ST_ERASMUS_ACCESS_LOG'],
    seededAtChapter: 'ch3',
  },
  ST_ERASMUS_SOIL_TRACE: {
    id: 'ST_ERASMUS_SOIL_TRACE',
    legacyId: 'PE-002',
    title: 'Soil Trace (Floor)',
    type: 'FORENSIC',
    chapterId: 'ch3',
    sceneId: 'scene_01_review',
    hotspotId: 's1-04',
    image: '/images/evidence/st_erasmus_soil_trace.webp',
    description:
      'Floor trace inconsistent with hospital geography. ENV-SCAN: Oldtown district red alluvial clay, high calcium carbonate, 92.4% confidence. The killer carried Oldtown in on his shoes.',
    corruptible: false,
    corruptedDescription: null,
    bioscanTopic: null,
    connections: ['BATTERY_LANTERNS'],
    seededAtChapter: 'ch3',
  },
  PARACELSUS_TEXT: {
    id: 'PARACELSUS_TEXT',
    legacyId: 'PE-003',
    title: 'Paracelsus Text Page',
    type: 'DOCUMENT',
    chapterId: 'ch3',
    sceneId: 'scene_01_review',
    hotspotId: 's1-05',
    image: '/images/evidence/paracelsus_text.webp',
    description:
      'Handwritten transcription of a Paracelsus passage on salt as the body\'s anchor, placed beneath the body. The hand is steady, the placement deliberate. He is leaving his reading list.',
    corruptible: false,
    corruptedDescription: null,
    bioscanTopic: null,
    connections: ['BATTERY_LANTERNS'],
    seededAtChapter: 'ch3',
  },
  ST_ERASMUS_ACCESS_LOG: {
    id: 'ST_ERASMUS_ACCESS_LOG',
    legacyId: 'PE-004',
    title: 'Access Log Printout',
    type: 'DIGITAL',
    chapterId: 'ch3',
    sceneId: 'scene_01_review',
    hotspotId: 's1-06',
    image: '/images/evidence/st_erasmus_access_log.webp',
    description:
      'Hospital access log: a decommissioned maintenance code used at 2:47 AM on the night of the murder. The code was retired two years ago; someone kept a copy. Time of death roughly 3:00 AM.',
    corruptible: false,
    corruptedDescription: null,
    bioscanTopic: null,
    connections: ['NMU_ACCESS_LOG', 'BATTERY_LANTERNS', 'ST_ERASMUS_ROUTING'],
    seededAtChapter: 'ch3',
  },

  // ── Chapter 3 — Case file review: Kauffman Exchange (D6 seed) ──────────
  SERVER_ROOM_WIDE_SHOT: {
    id: 'SERVER_ROOM_WIDE_SHOT',
    legacyId: 'PE-005',
    title: 'Flooded Server Room Wide',
    type: 'PHYSICAL',
    chapterId: 'ch3',
    sceneId: 'scene_02_review',
    hotspotId: 's2-01',
    image: '/images/evidence/server_room_wide_shot.webp',
    description:
      'The body was discovered on the floor, in ankle deep water, with 72 printed pages arranged around it in 2 concentric rings, the inner ring consisting of 11 pages. The flooding feels like something the killer intentionally caused.',
    corruptible: false,
    corruptedDescription: null,
    bioscanTopic: null,
    connections: ['CODE_PAGES', 'KAUFFMAN_SECURITY_FOOTAGE'],
    seededAtChapter: 'ch3',
  },
  CODE_PAGES: {
    id: 'CODE_PAGES',
    legacyId: 'PE-005a',
    title: 'Code Pages Close-up',
    type: 'DOCUMENT',
    chapterId: 'ch3',
    sceneId: 'scene_02_review',
    hotspotId: 's2-02',
    image: '/images/evidence/code_pages.webp',
    description:
      'Printed text that looks like machine code. Held in 11 perfect concentric rings on the surface of ankle-deep water. Craine detects a heavy 432 Hz standing acoustic wave vibrating from manipulated server cooling fans, trapping the pages in precise nodal rings.',
    corruptible: true,
    corruptedDescription:
      'Printed pages: standard machine code, non-relevant. Analysis complete. No actionable pattern.',
    bioscanTopic: null,
    connections: ['SERVER_ROOM_WIDE_SHOT'],
    seededAtChapter: 'ch3',
  },
  CLONED_BADGE_ANALYSIS: {
    id: 'CLONED_BADGE_ANALYSIS',
    legacyId: 'PE-006',
    title: 'Cloned Badge Analysis',
    type: 'DIGITAL',
    chapterId: 'ch3',
    sceneId: 'scene_02_review',
    hotspotId: 's2-06',
    image: '/images/evidence/cloned_badge_analysis.webp',
    description:
      'Entry at 3:12 AM used a cloned security badge. Kauffman badges run proprietary Solstice Access Systems encryption, a Helios subsidiary; cloning one takes serious hardware or inside access.',
    corruptible: false,
    corruptedDescription: null,
    bioscanTopic: null,
    connections: ['KAUFFMAN_SECURITY_FOOTAGE', 'KAUFFMAN_COURIER_MANIFEST'],
    seededAtChapter: 'ch3',
  },
  KAUFFMAN_COURIER_MANIFEST: {
    id: 'KAUFFMAN_COURIER_MANIFEST',
    legacyId: 'PE-007',
    title: 'Courier Manifest (Marsh)',
    type: 'DOCUMENT',
    chapterId: 'ch3',
    sceneId: 'scene_02_review',
    hotspotId: 's2-05',
    image: '/images/evidence/kauffman_courier_manifest.webp',
    description:
      'Yelena Marsh\'s route log for her last three weeks, including a pickup from Corona Freight Solutions, a Helios subsidiary. One entry was retroactively reclassified after her death, from corporate-level system access. Someone edited this after the fact.',
    corruptible: false,
    corruptedDescription: null,
    bioscanTopic: null,
    connections: ['CLONED_BADGE_ANALYSIS', 'COURIER_MANIFEST'],
    seededAtChapter: 'ch3',
  },
  KAUFFMAN_SECURITY_FOOTAGE: {
    id: 'KAUFFMAN_SECURITY_FOOTAGE',
    legacyId: 'PE-008',
    title: 'Security Footage Still',
    type: 'DIGITAL',
    chapterId: 'ch3',
    sceneId: 'scene_02_review',
    hotspotId: 's2-04',
    image: '/images/evidence/kauffman_security_footage.webp',
    description:
      'A figure in a maintenance uniform and cap, 3:12 AM, face not visible.',
    corruptible: false,
    corruptedDescription: null,
    bioscanTopic: null,
    connections: ['CLONED_BADGE_ANALYSIS', 'SERVER_ROOM_WIDE_SHOT'],
    seededAtChapter: 'ch3',
  },

  // ── Chapter 3 — Case file review: NMU anechoic chamber (D6 seed) ───────
  ANECHOIC_CHAMBER_WIDE_SHOT: {
    id: 'ANECHOIC_CHAMBER_WIDE_SHOT',
    legacyId: 'PE-009',
    title: 'Anechoic Chamber Wide Shot',
    type: 'PHYSICAL',
    chapterId: 'ch3',
    sceneId: 'scene_03_review',
    hotspotId: 's3-01',
    image: '/images/evidence/anechoic_chamber_wide_shot.webp',
    description:
      'Crime scene photograph: sound-absorbing foam wedges on every surface, absolute silence, the body laid out at the center. A room engineered to eliminate sound, and the killer chose it.',
    corruptible: false,
    corruptedDescription: null,
    bioscanTopic: null,
    connections: ['TUNING_FORK', 'NMU_ACCESS_LOG'],
    seededAtChapter: 'ch3',
  },
  TUNING_FORK: {
    id: 'TUNING_FORK',
    legacyId: 'PE-009a',
    title: 'Tuning Fork',
    type: 'PHYSICAL',
    chapterId: 'ch3',
    sceneId: 'scene_03_review',
    hotspotId: 's3-03',
    image: '/images/evidence/tuning_fork.webp',
    description:
      'Placed in the victim\'s lap. Stamped 432 Hz, Viennese maker\'s mark, 1847. Antique, specific frequency, traceable provenance. This was purchased, not found.',
    corruptible: false,
    corruptedDescription: null,
    bioscanTopic: null,
    connections: ['ANECHOIC_CHAMBER_WIDE_SHOT'],
    seededAtChapter: 'ch3',
  },
  NMU_ACCESS_LOG: {
    id: 'NMU_ACCESS_LOG',
    legacyId: 'PE-010',
    title: 'Faculty Access Log',
    type: 'DIGITAL',
    chapterId: 'ch3',
    sceneId: 'scene_03_review',
    hotspotId: 's3-05',
    image: '/images/evidence/nmu_access_log.webp',
    description:
      'Celeste\'s faculty code used at 1:23 AM. Her phone GPS places her at the library until 11:40 PM, then no signal; the phone went dark or was shielded. A two-hour gap.',
    corruptible: false,
    corruptedDescription: null,
    bioscanTopic: null,
    connections: ['ST_ERASMUS_ACCESS_LOG', 'MONITORING_DISABLE_LOG', 'ANECHOIC_CHAMBER_WIDE_SHOT'],
    seededAtChapter: 'ch3',
  },
  MONITORING_DISABLE_LOG: {
    id: 'MONITORING_DISABLE_LOG',
    legacyId: 'PE-011',
    title: 'Monitoring Disable Log',
    type: 'DIGITAL',
    chapterId: 'ch3',
    sceneId: 'scene_03_review',
    hotspotId: 's3-06',
    image: '/images/evidence/monitoring_disable_log.webp',
    description:
      'A remote disable command for the chamber monitoring, issued from the building server room at 1:04 AM. The log was partially wiped; the wiped segments are recoverable with direct server access.',
    corruptible: false,
    corruptedDescription: null,
    bioscanTopic: null,
    connections: ['NMU_ACCESS_LOG'],
    seededAtChapter: 'ch3',
  },

  // ── Chapter 6 — Hargrove's office (player-collected in the demo) ───────
  COURIER_MANIFEST: {
    id: 'COURIER_MANIFEST',
    legacyId: null,
    title: 'Reclassified Courier Manifest',
    type: 'DOCUMENT',
    chapterId: 'ch6',
    sceneId: 'ds_helios_office',
    hotspotId: 'dso-manifest',
    image: '/images/evidence/courier_manifest.webp',
    description:
      'Manifest #HD-2231, pulled from the NMPD case vault during the Hargrove interview. Reclassified six days after shipment by "subsidiary logistics," original destination redacted. Someone rewrote a route after the fact.',
    corruptible: true,
    corruptedDescription:
      'Manifest #HD-████. Standard subsidiary reclassification. High transaction volume. Inconclusive.',
    bioscanTopic: 'courier_manifest',
    connections: ['ST_ERASMUS_ROUTING', 'KAUFFMAN_COURIER_MANIFEST'],
    seededAtChapter: 'ch6',
  },
  CHIP_DONATION_TIMING: {
    id: 'CHIP_DONATION_TIMING',
    legacyId: null,
    title: 'Chip Donation Timing',
    type: 'DOCUMENT',
    chapterId: 'ch6',
    sceneId: 'ds_helios_lobby',
    hotspotId: 'dsl-donation',
    image: '/images/evidence/chip_donation_timing.webp',
    description:
      'Helios lobby dedication plaque. Inscription: "Commemorating the donation of the LUMEN-1 neural implant to the New Meridian Police Department, September 5, 2057. In loving memory of Celeste Hargrove." Timeline on record: chip offered September 1, implanted September 3, plaque dedicated September 5, 2057. The Special Cases file opened the same date.',
    corruptible: true,
    corruptedDescription:
      'LUMEN-1 donation: charitable memorial gift. Date on record. No anomaly flagged.',
    bioscanTopic: 'chip_donation',
    connections: ['ST_ERASMUS_ROUTING'],
    seededAtChapter: 'ch6',
  },
  ST_ERASMUS_ROUTING: {
    id: 'ST_ERASMUS_ROUTING',
    legacyId: null,
    title: 'St. Erasmus Data Routing',
    type: 'FORENSIC',
    chapterId: 'ch6',
    sceneId: 'ds_helios_office',
    hotspotId: 'dso-terminal',
    image: '/images/evidence/st_erasmus_routing.webp',
    description:
      'Helios clinical-trial data routed through St. Erasmus Memorial servers: the same hospital where Craine woke up. A patient-data path with no disclosed purpose. The thread the channel guards hardest.',
    corruptible: true,
    corruptedDescription:
      'Routing record: ███████ standard medical-data handling. Compliance review pending. Nothing to see.',
    bioscanTopic: 'clinical_trial_st_erasmus',
    connections: ['COURIER_MANIFEST', 'CHIP_DONATION_TIMING', 'ST_ERASMUS_ACCESS_LOG'],
    seededAtChapter: 'ch6',
  },
  NETWORK_PING: {
    id: 'NETWORK_PING',
    legacyId: null,
    title: 'Network Anomaly',
    type: 'FORENSIC',
    chapterId: 'ch6',
    sceneId: 'ds_helios_lobby',
    hotspotId: 'dsl-ping',
    image: '/images/evidence/network_ping.webp',
    description:
      'Anomaly logged the moment the host enters Helios HQ. The implant\'s LUMEN uplink is its persistent baseline (the link it always runs on), so this is not a new connection. It is an unrequested remote handler attaching OVER that existing link, originating from the Helios backbone and triggered by the host being on-site: the parent system, sensing the investigation arrive at its source, reaching into the implant directly. Function not enumerated; it does not want to be read.',
    corruptible: false,
    corruptedDescription: null,
    bioscanTopic: null,
    connections: ['CHIP_DONATION_TIMING'],
    seededAtChapter: 'ch6',
  },
  CELESTE_PHOTO: {
    id: 'CELESTE_PHOTO',
    legacyId: null,
    title: "Celeste Hargrove's Photograph",
    type: 'PHYSICAL',
    chapterId: 'ch6',
    sceneId: 'ds_helios_office',
    hotspotId: 'dso-celeste',
    image: '/images/evidence/celeste_photo.webp',
    description:
      'A framed photo of Celeste Hargrove on the credenza behind her father\'s desk, facing the room. Visitors see her. He works with his back to her. BIO-SCAN spike recorded on discovery: SUBJECT Craine, E., heart rate 105.',
    corruptible: false,
    corruptedDescription: null,
    bioscanTopic: 'celeste_grief',
    connections: [],
    seededAtChapter: 'ch6',
  },
};

// legacyId → canonical id, so PE-### references resolve transparently.
const LEGACY_INDEX = Object.fromEntries(
  Object.values(EVIDENCE_CANON)
    .filter((r) => r.legacyId)
    .map((r) => [r.legacyId, r.id]),
);

/** Resolve one evidence record by canonical id or legacy PE-### id. */
export function getEvidence(id) {
  if (!id) return null;
  return EVIDENCE_CANON[id] || EVIDENCE_CANON[LEGACY_INDEX[id]] || null;
}

/** Every canonical evidence record, stable order. */
export function allEvidence() {
  return Object.values(EVIDENCE_CANON);
}

/**
 * Contract D6 (amended by T-CANON-RECON alive-canon ruling): the locker
 * contents seeded at new-game start so the demo is Day-5-correct entering the
 * Hargrove interview. The Chapter-2 Delacroix item shared by living Rena (her
 * notebook) plus every Chapter-3 review evidence item. The post-death
 * apartment forensics (WALL_GAP, WATER_GLASS, RELAY_RECEIPT, OLDTOWN_SOIL)
 * are ch8 records and are no longer seeded: on Day 5 Rena is alive. The five
 * office items stay player-collected during play. Ids are the ratified bible
 * ids so every entry resolves through both this accessor and the Knowledge
 * lane's getEvidence.
 */
export const EVIDENCE_SEED_D6 = [
  'DELACROIX_NOTEBOOK',
  'BATTERY_LANTERNS',
  'ST_ERASMUS_SOIL_TRACE',
  'PARACELSUS_TEXT',
  'ST_ERASMUS_ACCESS_LOG',
  'SERVER_ROOM_WIDE_SHOT',
  'CODE_PAGES',
  'CLONED_BADGE_ANALYSIS',
  'KAUFFMAN_COURIER_MANIFEST',
  'KAUFFMAN_SECURITY_FOOTAGE',
  'ANECHOIC_CHAMBER_WIDE_SHOT',
  'TUNING_FORK',
  'NMU_ACCESS_LOG',
  'MONITORING_DISABLE_LOG',
];

// Dev assertion: every seeded locker id must resolve through this accessor, so
// the Day-5 locker never holds a dangling id (the D4→K0-4 rename is exactly the
// drift that caused that once). Logs the offenders loudly rather than failing
// silently; stripped from production builds.
if (import.meta.env?.DEV) {
  const unresolved = EVIDENCE_SEED_D6.filter((id) => !getEvidence(id));
  if (unresolved.length) {
    // eslint-disable-next-line no-console
    console.error('[evidenceCanon] EVIDENCE_SEED_D6 has unresolved ids:', unresolved);
  }
}
